import { RegistrationService } from 'services/main/endpoints/RegistrationService';
import { createToast, createErrorToast } from './createToast';
import imageCompression from 'browser-image-compression';
import * as Sentry from '@sentry/react';
import { saveToOutbox, processOutbox } from './outbox';
//
//  RETRY
//
//const INITIAL_DELAY = 5000;
const MAX_RETRIES = 3;

export const delay = ms => new Promise(res => setTimeout(res, ms));

const retryUpload = async (fn, setToastList, cookie, retries = MAX_RETRIES) => {
  try {
    return await fn(); // ← tenta direto, sem checar retries aqui
  } catch (error) {
    const status = error?.response?.status;

    if (status === 401) {
      createErrorToast(setToastList, 'Token inválido');
      Sentry.logger.info('Upload - token inválido', { empresa_id: cookie?.companies?.id });
      throw error;
    }

    // check aqui, no catch — e relança o erro original (não "Muitas tentativas")
    if (retries <= 0) {
      throw error;
    }

    await delay(5000);
    return retryUpload(fn, setToastList, cookie, retries - 1);
  }
};

//
// HELPERS
//
const ALLOWED_EXTENSIONS = ['jpeg', 'jpg', 'png', 'bmp', 'qoi', 'gif', 'tga', 'pbm', 'webp', 'tiff', 'tif'];

const validateFile = (file, cookie, setToastList) => {
  const ext = file.name.split('.').pop().toLowerCase();

  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    createErrorToast(setToastList, 'Formato não permitido. Use: JPEG, PNG, BMP, GIF, WebP, TIFF, QOI, TGA, PBM.');
    Sentry.logger.info('Upload - formato inválido', { empresa_id: cookie?.companies?.id });
    throw new Error('Formato de arquivo não suportado.');
  }
};

const compressImage = async file => {
  try {
    const compressed = await imageCompression(file, {
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      initialQuality: 0.9,
    });

    return new File([compressed], file?.name, { type: compressed?.type });
  } catch (err) {
    console.error('Erro ao comprimir imagem:', err);
    return file;
  }
};

//
//  UPLOAD CORE
//
export const uploadImage = async ({ file, filePath, token }) => {
  const response = await RegistrationService.uploadSignature({
    filePath,
    file,
    token,
  });

  if (response.status === 401) {
    throw new Error('Token inválido');
  }

  return response;
};

//
// 🚀 MAIN FUNCTION
//
const handleImageUpload = async (
  imageData,
  mediaType,
  empresaId,
  setToastList,
  contextSetToastList,
  compress = true,
  cookie,
) => {
  if (!imageData || typeof imageData === 'string') {
    return imageData;
  }

  let file = imageData.file;

  // validação
  validateFile(file, cookie, setToastList);

  // compressão
  if (compress) {
    file = await compressImage(file);
  }

  // toast loading
  const loadingToast = createToast({
    type: 'loading',
    message: 'Estamos enviando sua imagem...',
  });

  setToastList(prev => [...prev, loadingToast]);

  const doUpload = () =>
    uploadImage({
      file,
      filePath: imageData.cloudPath,
      token: cookie?.authToken,
    });

  try {
    const response = await retryUpload(doUpload, setToastList, cookie);
    return response;
  } catch (error) {
    //  fallback → OUTBOX
    try {
      await saveToOutbox({
        file,
        filePath: imageData.cloudPath,
        empresaId,
      });
      processOutbox(cookie, contextSetToastList);
      createErrorToast(contextSetToastList, 'Erro ao enviar a foto. Vamos tentar novamente em segundo plano.');
    } catch (outboxError) {
      Sentry.captureException(outboxError, {
        tags: { area: 'outbox', action: 'save_image' },
        extra: { empresa_id: cookie?.companies?.id },
      });
      createErrorToast(contextSetToastList, 'Não foi possível enviar nem salvar a foto para reenvio. Tente novamente.');
    }

    return null;
  } finally {
    setToastList(prev => prev.filter(t => t.id !== loadingToast.id));
  }
};

export { handleImageUpload };
